import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { useIsFocused } from '@react-navigation/native';

import { useLeagueUpcomingEventsQuery } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import { GetLobbyContentQuery } from '@/api/lobby/query.generated';
import { useLiveEventsPropsUpdate } from '@/feature/betslip-pickem/hooks/use-live-props-update';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { CardSegmentTitleType } from '@/feature/fantasy-event-sheets/components/ufc-events/CardSegmentTitle';
import { useGQLRequestPolicy } from '@/feature/guest-user/hooks/use-gql-request-policy';
import { lobbyId, useLobbySectionSelection } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { useWhatsNewStore } from '@/feature/whats-new/hooks/use-whats-new-store';
import { LeaguePositionFilters, useGetOrderedLeagues } from '@/hooks/use-fantasy-league-configs';
import { useResumeEffectWithDelay } from '@/hooks/use-resume';
import { IndividualVersusEvent, League } from '@/types/api.generated';
import { sortEvents } from '@/utils/sortEvents';
import { sortLobbyPlayers } from '@/utils/sortPlayers';

import { getPositionFilters } from '../utils/filters';
import { generateLobbyPlayerCards } from '../utils/lobbyPlayers';
import { useFantasyLobbyQueryWithVariables } from './use-fantasy-lobby-query-with-variables';
import { useUFCGroupedPlayers } from './use-sorted-lobby-players';

type LobbyDataContextValue = {
    sortedLeagueEvents: EventInfo[];
    sortedLobbyPlayers: PlayerWithTeamAndEvent[];
    sortedGroupedUFCPlayers: (CardSegmentTitleType | IndividualVersusEvent)[];
    sharedEntries: GetLobbyContentQuery['getSharedEntries'];
    hasLobbyData: boolean;
    leagueEventsLoading: boolean;
    hasStaleData: boolean;
    leagueId: League;
    isLobby: boolean;
    positionFilters: LeaguePositionFilters;
};

const LobbyDataContext = createContext<LobbyDataContextValue | undefined>(undefined);

/**
 * Provider that handles data fetching, live updates, and event sorting for the lobby.
 * Re-renders when the selected league/tile changes or when query data updates.
 */
export const LobbyDataProvider = ({ children }: { children: ReactNode }) => {
    const selectedTile = useLobbySectionSelection(state => state.selectedTile);
    const isLobby = selectedTile === lobbyId;
    const leagueId = (selectedTile !== lobbyId ? selectedTile : undefined) as League;

    const isFocused = useIsFocused();

    const requestPolicy = useGQLRequestPolicy('cache-and-network');

    const setCanDisplayWhatsNewCard = useWhatsNewStore(state => state.actions.setLobbyReady);

    const { leagues } = useGetOrderedLeagues();
    const positionFilters = useMemo(() => getPositionFilters(leagueId, leagues), [leagueId, leagues]);

    // Fetch top 10, lobby players, shared lineups
    const [{ data: lobbyData, fetching: fetchingLobbyData }, fetchLobbyContent] = useFantasyLobbyQueryWithVariables({
        requestPolicy,
        /**
         * paused initially, to avoid double fetching as
         * this will be triggered by the `appResumeEffect`
         */
        pause: true,
    });

    const hasLobbyData = !!lobbyData;
    const sharedEntries = lobbyData?.getSharedEntries;

    const lobbyEvents = useMemo(() => {
        return mergeEvents(lobbyData?.getTopTrendingPlayersData, lobbyData?.getUpcomingLobbyEventsV2);
    }, [lobbyData?.getTopTrendingPlayersData, lobbyData?.getUpcomingLobbyEventsV2]);

    useLiveEventsPropsUpdate({
        events: lobbyEvents,
        options: {
            subscribeToGameClockUpdates: isLobby && isFocused,
            subscribeToMarketUpdates: isLobby && isFocused,
            debugTag: 'LobbyDataProvider-lobby',
        },
    });

    // Fetch specific league events and players
    const [{ data: leagueEventsWithPlayers, fetching: fetchingLeagueEventsData }, fetchLeagueContent] =
        useLeagueUpcomingEventsQuery({
            variables: { league: leagueId },
            requestPolicy,
            pause: true,
        });

    useLiveEventsPropsUpdate({
        events: leagueEventsWithPlayers?.getUpcomingEventsV2,
        options: {
            subscribeToGameClockUpdates: !isLobby && isFocused,
            subscribeToMarketUpdates: !isLobby && isFocused,
            debugTag: `LobbyDataProvider-${leagueId}`,
        },
    });

    const hasStaleData =
        leagueEventsWithPlayers?.getUpcomingEventsV2.length !== undefined &&
        leagueEventsWithPlayers.getUpcomingEventsV2[0]?.league !== leagueId &&
        leagueEventsWithPlayers.getUpcomingEventsV2.length !== 0;

    const sortedLeagueEvents = useMemo(() => {
        if (hasStaleData) {
            return [];
        }
        return (leagueEventsWithPlayers?.getUpcomingEventsV2 ?? []).toSorted(sortEvents);
    }, [hasStaleData, leagueEventsWithPlayers?.getUpcomingEventsV2]);

    const sortedLobbyPlayers = useMemo(() => {
        return generateLobbyPlayerCards(lobbyData?.getUpcomingLobbyEventsV2 ?? []).toSorted(sortLobbyPlayers);
    }, [lobbyData?.getUpcomingLobbyEventsV2]);

    const sortedGroupedUFCPlayers = useUFCGroupedPlayers(sortedLeagueEvents);

    useEffect(() => {
        if (!fetchingLobbyData && hasLobbyData) {
            //Display the 'Whats New' with a delay, so the heavy lobby animation does not compete with
            //the 'Whats New' card animating in
            setTimeout(() => {
                setCanDisplayWhatsNewCard(true);
            }, 600);
        }
    }, [fetchingLobbyData, hasLobbyData, setCanDisplayWhatsNewCard]);

    const fetchData = useCallback(() => {
        if (isLobby) {
            fetchLobbyContent();
        } else {
            fetchLeagueContent();
        }
    }, [fetchLeagueContent, fetchLobbyContent, isLobby]);

    useResumeEffectWithDelay(fetchData);

    const isLeagueDataLoading = fetchingLeagueEventsData || leagueEventsWithPlayers === undefined || hasStaleData;
    const leagueEventsLoading = !isLobby && isLeagueDataLoading;

    const value = useMemo<LobbyDataContextValue>(
        () => ({
            sortedLeagueEvents,
            sortedLobbyPlayers,
            sortedGroupedUFCPlayers,
            sharedEntries,
            hasLobbyData,
            leagueEventsLoading,
            hasStaleData,
            leagueId,
            isLobby,
            positionFilters,
        }),
        [
            sortedLeagueEvents,
            sortedLobbyPlayers,
            sortedGroupedUFCPlayers,
            sharedEntries,
            hasLobbyData,
            leagueEventsLoading,
            hasStaleData,
            leagueId,
            isLobby,
            positionFilters,
        ]
    );

    return <LobbyDataContext.Provider value={value}>{children}</LobbyDataContext.Provider>;
};

export const useLobbyData = (): LobbyDataContextValue => {
    const context = useContext(LobbyDataContext);
    if (context === undefined) {
        throw new Error('useLobbyData must be used within a LobbyDataProvider');
    }
    return context;
};

const mergeEvents = (...events: (EventInfo[] | undefined)[]): EventInfo[] => {
    return [...events].filter(Boolean).flat();
};
